import type { Auth, User as FirebaseAuthUser } from "firebase/auth"
import type { Firestore } from "firebase/firestore"
import type { Functions } from "firebase/functions"
import { createContext, useContext } from "react"

import type { Role } from "@timebanken/services"

export interface FirebaseContextType {
  db: Firestore
  auth: Auth
  userId: string | null
  user: FirebaseAuthUser | null
  userRole: Role | null
  /** Set only from the Firebase Auth superAdmin claim, never tenant role data. */
  isPlatformAdministrator?: boolean
  /** Trusted private-assignment IDs, distinct from ordinary user membership. */
  managedTeamIds?: string[]
  userStatus: string | null
  userFirstName: string | null
  userLastName: string | null
  isAuthReady: boolean
  isLoadingUser: boolean
  isLoggingOut: boolean
  logout: () => void
  userAnnualHourOverrides: { [year: string]: number }
  globalAnnualHours: { [year: string]: number }
  functions: Functions
  databaseId?: string
  config: Record<string, unknown> | null
  /** GDPR phone sharing consent: true = share, false = decline, null/undefined = not yet chosen */
  phoneShareConsent: boolean | null | undefined
  /** Whether the current user has a phone number set */
  hasPhone: boolean
  /** Whether the current user needs to make a phone consent choice (has phone but no consent decision) */
  needsPhoneConsent: boolean
}

export const FirebaseContext = createContext<FirebaseContextType | null>(null)

export const useFirebase = (): FirebaseContextType => {
  const context = useContext(FirebaseContext)
  if (context === null) {
    throw new Error("useFirebase must be used within a FirebaseProvider")
  }
  return context
}

export const useAuth = () => {
  const { user, userRole } = useFirebase()
  return { user, userRole }
}
